import React, { useEffect, useRef, useState } from "react";
import AddTaskManagerModal from "../../components/TaskManagement/AddTaskManagerModal";
import { fetchUsers } from "../../store/slices/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  addTaskManager,
  getTaskManager,
  updateTaskManagementStatus,
  updateTaskAssignees,
  getTaskMetaData,
  updateTaskAsDuplicate,
  updateTaskManager,
  setTaskManagementState,
  getRepetitiveTaskManager,
  updateRepetitiveTaskManager,
  updateTaskRequestedEndDate,
  getAuthorApprovalTasks,
} from "../../store/slices/taskmanagementSlice";
import toast from "react-hot-toast";
import { IUserRef } from "../../models/userData.model";
import {
  IRepetitiveTaskManager,
  ITaskManagement,
  RepetitiveTaskDays,
  RepetitiveTaskType,
} from "../../models/taskManagement.model";
import TaskManagerTimeInputModal from "../../components/TaskManagement/TaskManagerTimeInputModal";
import DuplicateTaskManagerModal from "../../components/TaskManagement/DuplicateTaskManagerModal";
import {
  buildFieldChanges,
  FormChangeEvent,
  handleFormChangeUtil,
  removeAttachment,
} from "../../utils/taskManagementUtils";
import TaskManagerLogsModal from "../../components/TaskManagement/TaskManagerLogsModal";
import useOutsideClick from "../../components/Assets/useOutsideClick";
import { useNavigate } from "react-router-dom";
import TaskManagerStatusFilter from "../../components/TaskManagement/TaskManagerStatusFilter";
import TaskManagerApprovalTable from "../../components/TaskManagement/TaskManagerApprovalTable";
import TaskManagerFilters from "../../components/TaskManagement/TaskManagerFilters";
import TaskManagerTable from "../../components/TaskManagement/TaskManagerTable";
import TaskManagerDateExtensionApprovalTable from "../../components/TaskManagement/TaskManagerDateExtensionApprovalTable";

const TaskManagement: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);

  const today = new Date();

  const [form, setForm] = useState<ITaskManagement>({
    taskName: "",
    assignee: "",
    followers: [] as string[],
    type: "" as string,
    description: "",
    priority: "hot",
    status: "ongoing",
    startDate: today,
    endDate: today,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // number of tasks per page
  const [totalPages, setTotalPages] = useState(1); // will be set from backend response

  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<ITaskManagement | null>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [selectedTaskForLogs, setSelectedTaskForLogs] = useState<string | null>(
    null
  );
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [followerFilter, setFollowerFilter] = useState("");
  const [authorFilter, setAuthorFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [isRepetitiveTask, setIsRepetitiveTask] = useState(false);
  const [selectedDays, setSelectedDays] = useState<RepetitiveTaskDays[]>([]);
  const [repetitionType, setRepetitionType] = useState<RepetitiveTaskType>(
    RepetitiveTaskType.weekly
  );
  const [noOfDays, setNoOfDays] = useState<number>(0);
  const [files, setFiles] = useState<FileList | null>(null);

  const [showRepetitiveTasks, setShowRepetitiveTasks] = useState(false);

  const [monthlyDate, setMonthlyDate] = useState<Date | null>(null);
  const [yearlyDate, setYearlyDate] = useState<Date | null>(null);

  const handleDaySelection = (day: RepetitiveTaskDays) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleRepetitionTypeChange = (type: RepetitiveTaskType) => {
    setRepetitionType(type);
    setSelectedDays([]);
    setMonthlyDate(null);
    setYearlyDate(null);
  };

  const { tasks, socketApprovalTasks } = useSelector(
    (state: RootState) => state.TaskManagement
  );
  const users = useSelector((state: RootState) => state.users.user);
  const { statusFilter } = useSelector(
    (state: RootState) => state.TaskManagement
  );
  const { repetitiveTasks } = useSelector(
    (state: RootState) => state.TaskManagement
  );

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(dropdownRef, () => setOpenDropdown(null));

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(getTaskMetaData());
    dispatch(getAuthorApprovalTasks());
  }, [dispatch]);

  useEffect(() => {
    const params = {
      status: statusFilter,
      follower: followerFilter,
      author: authorFilter,
      assignee: assigneeFilter,
      startDate: startDateFilter,
      endDate: endDateFilter,
      page: currentPage,
      limit: pageSize,
    };

    if (showRepetitiveTasks) {
      dispatch(getRepetitiveTaskManager(params))
        .unwrap()
        .then((res: any) => setTotalPages(res.pagination.totalPages || 1))
        .catch((err) => console.error("Failed to fetch repetitive tasks", err));
    } else {
      dispatch(getTaskManager(params))
        .unwrap()
        .then((res: any) => setTotalPages(res.pagination.totalPages || 1))
        .catch((err) => console.error("Failed to fetch normal tasks", err));
    }
  }, [
    dispatch,
    statusFilter,
    assigneeFilter,
    followerFilter,
    authorFilter,
    startDateFilter,
    endDateFilter,
    showRepetitiveTasks,
    currentPage,
    pageSize,
  ]);

  // useEffect(() => {
  //   if (!socket.connected) socket.connect();

  //   socket.on("task-updated", (data) => {
  //     dispatch(recieveUnpushTask(data.task));
  //   });

  //   socket.on("extend-date", (data) => {
  //     if (data?.task) {
  //       dispatch(UpdateEndDateApproval(data.task));
  //     }
  //   });

  //   return () => {
  //     socket.off("task-updated");
  //     socket.off("extend-date");
  //   };
  // }, [dispatch]);

  // useEffect(() => {
  //   dispatch(getTaskNotificationCount());

  //   if (!socket.connected) socket.connect();

  //   socket.on("task-updated", (data) => {
  //     if (!data?.task) return;

  //     dispatch(recieveUnpushTask(data.task));
  //     if (data.task.status === "waiting for approval") {
  //       dispatch(incrementNotification());
  //     } else {
  //       dispatch(decrementNotification());
  //     }
  //   });

  //   socket.on("extend-date", (data) => {
  //     if (!data?.task) return;

  //     dispatch(UpdateEndDateApproval(data.task));
  //     if (data.task.requestedEndDate) {
  //       dispatch(incrementNotification());
  //     } else {
  //       dispatch(decrementNotification());
  //     }
  //   });

  //   return () => {
  //     socket.off("task-updated");
  //     socket.off("extend-date");
  //   };
  // }, [dispatch]);

  const handleAssigneeFilterChange = (assigneeId: string) => {
    setAssigneeFilter(assigneeId);
  };
  const handleFollowerFilterChange = (assigneeId: string) => {
    setFollowerFilter(assigneeId);
  };

  const handleAuthorFilterChange = (authorId: string) => {
    setAuthorFilter(authorId);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDateFilter(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDateFilter(e.target.value);
  };

  const clearDateFilters = () => {
    setStartDateFilter("");
    setEndDateFilter("");
  };

  const handleStatusFilterChange = (
    filter:
      | ""
      | "ongoing"
      | "waiting for approval"
      | "completed"
      | "duplicate"
      | "overdue"
  ) => {
    dispatch(
      setTaskManagementState({
        statusFilter: filter,
      })
    );
  };

  const onSuccess = () => {
    setModalOpen(false);
  };

  const onCancel = () => {
    setModalOpen(false);
    setEditMode(false);
    setTaskToEdit(null);
    clearForm();
    setFiles(null);
  };

  const clearForm = () => {
    setForm({
      taskName: "",
      assignee: "",
      followers: [],
      type: "",
      description: "",
      priority: "hot",
      status: "ongoing",
      startDate: today,
      endDate: today,
    });
    setNoOfDays(0);
    setIsRepetitiveTask(false);
    setRepetitionType(RepetitiveTaskType.weekly);
    setSelectedDays([]);
    setMonthlyDate(null);
    setYearlyDate(null);
  };

  const handleTaskSelection = (taskId: string, isSelected: boolean) => {
    setSelectedTasks((prev) =>
      isSelected ? [...prev, taskId] : prev.filter((id) => id !== taskId)
    );
  };

  const handleSelectAllTasks = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedTasks(tasks.map((task) => task._id!));
    } else {
      setSelectedTasks([]);
    }
  };

  // const TaskBulkAssigneeUpdate = async () => {
  //   if (!bulkAssignee) {
  //     toast.error("Please select an assignee");
  //     return;
  //   }

  //   await dispatch(
  //     updateTaskAssignees({
  //       taskIds: selectedTasks,
  //       assignee: bulkAssignee,
  //     })
  //   ).unwrap();
  //   await dispatch(getTaskManager({}));
  //   setSelectedTasks([]);
  //   setBulkAssignee("");
  // };

  const updateTaskAssignee = async (
    taskId: string,
    assignee: string,
    oldAssignee: string
  ) => {
    await dispatch(
      updateTaskAssignees({
        taskIds: [taskId],
        assignee: assignee,
        change: {
          field: "assignee",
          oldValue: oldAssignee,
          newValue: assignee,
        },
      })
    ).unwrap();
    await dispatch(
      getTaskManager({
        status: statusFilter,
        assignee: assigneeFilter,
        follower: followerFilter,
        author: authorFilter,
        startDate: startDateFilter,
        endDate: endDateFilter,
        page: currentPage,
        limit: pageSize,
      })
    );
  };
  const handleChange = (e: FormChangeEvent) => {
    handleFormChangeUtil(e, form, setForm);
  };

  const handleOpenCompleteModal = (task: ITaskManagement) => {
    setSelectedTaskId(task._id!);
    setIsTimeModalOpen(true);
  };

  const handleOpenDuplicateModal = (task: ITaskManagement) => {
    setCurrentTask(task._id!);
    setIsDuplicateModalOpen(true);
  };
  const handleTaskClick = (task: ITaskManagement | IRepetitiveTaskManager) => {
    if (showRepetitiveTasks) {
      navigate(`/task-management/${task._id}?type=repetitive`);
    } else {
      navigate(`/task-management/${task._id}`);
    }
  };

  const handleConfirmDuplicate = async (
    currentTaskId: string,
    duplicateOfTaskId: string,
    remark: string
  ) => {
    await dispatch(
      updateTaskAsDuplicate({ currentTaskId, duplicateOfTaskId, remark })
    ).unwrap();
    await dispatch(
      getTaskManager({
        status: statusFilter,
        assignee: assigneeFilter,
        follower: followerFilter,
        author: authorFilter,
        startDate: startDateFilter,
        endDate: endDateFilter,
        page: currentPage,
        limit: pageSize,
      })
    );
    setIsDuplicateModalOpen(false);
    setCurrentTask(null);
  };

  const handleUpdateStatus = (
    task: ITaskManagement,
    decision: "approved" | "rejected"
  ) => {
    dispatch(
      updateTaskManagementStatus({ taskId: task._id!, decision })
    ).unwrap();
  };

  const updateStatusAndTime = async (time: string) => {
    if (selectedTaskId) {
      await dispatch(
        updateTaskManagementStatus({
          taskId: selectedTaskId,
          approxTime: time,
        })
      ).unwrap();
      await dispatch(
        getTaskManager({
          status: statusFilter,
          assignee: assigneeFilter,
          follower: followerFilter,
          author: authorFilter,
          startDate: startDateFilter,
          endDate: endDateFilter,
          page: currentPage,
          limit: pageSize,
        })
      );
    }
    setIsTimeModalOpen(false);
    setSelectedTaskId(null);
  };

  const handleCreateTask = async (files: File[], keepOpen: boolean = false) => {
    if (!form.taskName.trim()) {
      toast.error("Task Name is required.");
      return;
    }
    if (!form.assignee) {
      toast.error("Please select an assignee.");
      return;
    }
    if (!form.type) {
      toast.error("Please select a task type.");
      return;
    }
    if (!form.priority) {
      toast.error("Please select a task priority.");
      return;
    }
    if (!form.startDate) {
      toast.error("Please select a start date.");
      return;
    }
    if (!form.endDate && !isRepetitiveTask) {
      toast.error("Please select an end date.");
      return;
    }

    const formData = new FormData();

    formData.append("taskName", form.taskName);
    formData.append("assignee", form.assignee);
    formData.append("type", form.type);
    formData.append("description", form.description || "");
    formData.append("priority", form.priority);
    formData.append("status", form.status || "ongoing");
    if (form.startDate) formData.append("startDate", form.startDate.toString());
    if (form.endDate) formData.append("endDate", form.endDate.toString());

    if (form.followers.length > 0) {
      formData.append("followers", JSON.stringify(form.followers));
    }

    if (isRepetitiveTask) {
      formData.append("noOfDays", String(noOfDays));

      if (
        repetitionType === RepetitiveTaskType.weekly &&
        selectedDays.length > 0
      ) {
        formData.append(
          "typeOfRepetition",
          JSON.stringify({
            type: repetitionType,
            data: {
              days: selectedDays,
              noOfDays: noOfDays,
            },
          })
        );
      } else if (repetitionType === RepetitiveTaskType.monthly && monthlyDate) {
        formData.append(
          "typeOfRepetition",
          JSON.stringify({
            type: repetitionType,
            data: {
              date: monthlyDate.toISOString(),
              noOfDays: noOfDays,
            },
          })
        );
      } else if (repetitionType === RepetitiveTaskType.yearly && yearlyDate) {
        formData.append(
          "typeOfRepetition",
          JSON.stringify({
            type: repetitionType,
            data: {
              date: yearlyDate.toISOString(),
              noOfDays: noOfDays,
            },
          })
        );
      }
    }

    files.forEach((file) => {
      formData.append("attachments", file);
      formData.append("attachmentNames", file.name);
    });

    try {
      await dispatch(addTaskManager(formData)).unwrap();
      if (!keepOpen) {
        clearForm();
        onSuccess();
        setFiles(null);
      } else {
        setForm((prev) => ({
          ...prev,
          taskName: "",
          startDate: today,
          endDate: today,
          priority: "hot",
        }));
      }
      await dispatch(getTaskManager({ status: statusFilter }));
    } catch (error: any) {
      console.error("Error creating task:", error);
    }
  };

  const handleUpdateTask = async () => {
    if (!taskToEdit?._id) return;

    if (!form.taskName.trim()) {
      toast.error("Task Name is required.");
      return;
    }
    if (!form.assignee) {
      toast.error("Please select an assignee.");
      return;
    }
    if (!form.type) {
      toast.error("Please select a task type.");
      return;
    }
    if (!form.priority) {
      toast.error("Please select a task priority.");
      return;
    }
    if (!form.startDate) {
      toast.error("Please select a start date.");
      return;
    }

    const changes = buildFieldChanges(taskToEdit, form);
    if (changes.length === 0) {
      toast("No changes detected");
      return;
    }

    if (isRepetitiveTask) {
      await dispatch(
        updateRepetitiveTaskManager({
          id: taskToEdit._id,
          data: form as IRepetitiveTaskManager,
          changes,
        })
      ).unwrap();
    } else {
      await dispatch(
        updateTaskManager({ id: taskToEdit._id, data: form, changes })
      ).unwrap();
    }

    if (showRepetitiveTasks) {
      await dispatch(
        getRepetitiveTaskManager({
          status: statusFilter,
          follower: followerFilter,
          assignee: assigneeFilter,
          startDate: startDateFilter,
          endDate: endDateFilter,
          page: currentPage,
          limit: pageSize,
        })
      );
    } else {
      await dispatch(
        getTaskManager({
          status: statusFilter,
          follower: followerFilter,
          assignee: assigneeFilter,
          startDate: startDateFilter,
          endDate: endDateFilter,
          page: currentPage,
          limit: pageSize,
        })
      );
    }
    setModalOpen(false);
    setEditMode(false);
    setTaskToEdit(null);
  };

  const handleRemoveAttachment = async (attachmentId: string) => {
    if (!taskToEdit?._id) return;

    try {
      await removeAttachment(
        dispatch,
        taskToEdit._id,
        attachmentId,
        isRepetitiveTask
      );

      if (editMode && taskToEdit) {
        const updatedAttachments =
          form.attachments?.filter((att: any) => att._id !== attachmentId) ||
          [];

        setForm({
          ...form,
          attachments: updatedAttachments,
        });
      }
    } catch (error) {
      throw error;
    }
  };

  const handleDateExtensionApproval = async (
    taskId: string,
    decision: "approved" | "rejected"
  ) => {
    try {
      await dispatch(
        updateTaskRequestedEndDate({
          taskId,
          decision,
          status: statusFilter,
        })
      ).unwrap();
      await dispatch(
        getTaskManager({
          status: statusFilter,
          assignee: assigneeFilter,
          follower: followerFilter,
          author: authorFilter,
          startDate: startDateFilter,
          endDate: endDateFilter,
          page: currentPage,
          limit: pageSize,
        })
      );

      toast.success(
        `Request ${decision === "approved" ? "approved" : "rejected"}`
      );
    } catch (err) {
      console.error("Failed to update requested end date decision:", err);
      toast.error("Failed to update requested end date");
    }
  };

  const handleToggleRepetitiveTasks = (checked: boolean) => {
    setShowRepetitiveTasks(checked);
  };

  const calculateDaysLeft = (endDate?: Date | string | null): string => {
    if (!endDate) return "-";

    const today = new Date();
    const taskEndDate = new Date(endDate);

    const diffTime = taskEndDate.getTime() - today.getTime();
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days} day(s)`;
    if (days === 0) return "Today";
    return `Overdue by ${Math.abs(days)} day(s)`;
  };

  return (
    <div className="container max-w-full mx-auto px-6 pb-6">
      <TaskManagerApprovalTable
        tasks={socketApprovalTasks}
        onTaskClick={handleTaskClick}
        onUpdateStatus={handleUpdateStatus}
      />
      <TaskManagerDateExtensionApprovalTable
        tasks={tasks}
        onTaskClick={handleTaskClick}
        handleDateExtensionApproval={handleDateExtensionApproval}
      />
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center mt-6 mb-4">
        <h3 className="text-lg font-semibold text-gray-800 text-center sm:text-left">
          Task Management
        </h3>
        <Button
          type={ButtonType.Submit}
          onClick={() => setModalOpen(true)}
          className="text-sm w-full sm:w-auto sm:justify-self-end"
        >
          Add Task
        </Button>
      </div> */}

      {modalOpen && (
        <AddTaskManagerModal
          form={form}
          setForm={setForm}
          onCancel={onCancel}
          handleChange={handleChange}
          handleRemoveAttachment={editMode ? handleRemoveAttachment : undefined}
          handleCreateTask={editMode ? handleUpdateTask : handleCreateTask}
          isEdit={editMode}
          isRepetitiveTask={isRepetitiveTask}
          setIsRepetitiveTask={setIsRepetitiveTask}
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          repetitionType={repetitionType}
          handleDaySelection={handleDaySelection}
          handleRepetitionTypeChange={handleRepetitionTypeChange}
          monthlyDate={monthlyDate}
          setMonthlyDate={setMonthlyDate}
          yearlyDate={yearlyDate}
          setYearlyDate={setYearlyDate}
          noOfDays={noOfDays}
          setNoOfDays={setNoOfDays}
          files={files}
          setFiles={setFiles}
        />
      )}

      <TaskManagerFilters
        users={users}
        openAddTaskModal={() => setModalOpen(true)}
        assigneeFilter={assigneeFilter}
        followerFilter={followerFilter}
        authorFilter={authorFilter}
        startDateFilter={startDateFilter}
        endDateFilter={endDateFilter}
        onAssigneeChange={handleAssigneeFilterChange}
        onFollowerChange={handleFollowerFilterChange}
        onAuthorChange={handleAuthorFilterChange}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onClearDates={clearDateFilters}
      />

      <div className="overflow-x-auto md:block bg-white rounded-xl shadow-sm border border-gray-100">
        <TaskManagerStatusFilter
          resetPagination={() => setCurrentPage(1)}
          statusFilter={statusFilter}
          onChange={handleStatusFilterChange}
          handleToggleRepetitiveTasks={handleToggleRepetitiveTasks}
          showRepetitiveTasks={showRepetitiveTasks}
        />
        {/* <BulkAssignee
          selectedCount={selectedTasks.length}
          assigneeList={users}
          selectedAssignee={bulkAssignee}
          setSelectedAssignee={setBulkAssignee}
          onUpdate={TaskBulkAssigneeUpdate}
          onCancel={() => setSelectedTasks([])}
        /> */}
        <div>
          <TaskManagerTable
            updateAssignee={updateTaskAssignee}
            users={users}
            showRepetitiveTasks={showRepetitiveTasks}
            repetitiveTasks={repetitiveTasks}
            selectedTasks={selectedTasks}
            openDropdown={openDropdown}
            onTaskClick={handleTaskClick}
            onTaskSelection={handleTaskSelection}
            onSelectAllTasks={handleSelectAllTasks}
            onOpenCompleteModal={handleOpenCompleteModal}
            onUpdateStatus={handleUpdateStatus}
            onEditTask={(task: ITaskManagement) => {
              setForm({
                ...task,
                assignee: (task.assignee as unknown as IUserRef)?._id,
                followers: task.followers.map((follower: any) => follower._id),
                attachments: task.attachments || [],
              });
              setTaskToEdit(task);
              setEditMode(true);
              setModalOpen(true);
            }}
            onEditRepetitiveTask={(repeat: IRepetitiveTaskManager) => {
              setForm({
                ...repeat,
                assignee: (repeat.assignee as unknown as IUserRef)?._id,
                followers: repeat.followers.map(
                  (follower: any) => follower._id
                ),
                attachments: repeat.attachments || [],
                startDate: repeat.startDate ?? null,
                endDate: repeat.endDate ?? null,
              });
              setTaskToEdit({
                ...repeat,
                startDate: repeat.startDate ?? null,
                endDate: repeat.endDate ?? null,
              });
              setIsRepetitiveTask(true);
              setRepetitionType(
                repeat.typeOfRepetition?.type || RepetitiveTaskType.weekly
              );
              setSelectedDays(repeat.typeOfRepetition?.data?.days || []);
              setNoOfDays(repeat.typeOfRepetition?.data?.noOfDays || 0);
              setMonthlyDate(
                repeat.typeOfRepetition?.data?.date
                  ? new Date(repeat.typeOfRepetition.data.date)
                  : null
              );
              setYearlyDate(
                repeat.typeOfRepetition?.data?.date
                  ? new Date(repeat.typeOfRepetition.data.date)
                  : null
              );
              setEditMode(true);
              setModalOpen(true);
            }}
            onOpenDuplicateModal={handleOpenDuplicateModal}
            onSetOpenDropdown={setOpenDropdown}
            onSetSelectedTaskForLogs={setSelectedTaskForLogs}
            onSetIsLogModalOpen={setIsLogModalOpen}
            calculateDaysLeft={calculateDaysLeft}
          />
          <div className="flex justify-center items-center mt-4 gap-4 text-sm p-4">
            <button
              className={`px-2.5 py-1.5 rounded-md transition-colors duration-200 ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Prev
            </button>

            <span className="text-gray-600">
              Page <span className="font-semibold">{currentPage}</span> /{" "}
              <span className="font-semibold">{totalPages}</span>
            </span>

            <button
              className={`px-2.5 py-1.5 rounded-md transition-colors duration-200 ${
                currentPage === totalPages
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>

        <TaskManagerTimeInputModal
          isOpen={isTimeModalOpen}
          onClose={() => setIsTimeModalOpen(false)}
          onSubmit={updateStatusAndTime}
        />
        <DuplicateTaskManagerModal
          isOpen={isDuplicateModalOpen}
          onClose={() => {
            setIsDuplicateModalOpen(false);
            setCurrentTask(null);
          }}
          tasks={tasks}
          currentTaskId={currentTask}
          onConfirm={handleConfirmDuplicate}
        />
        <TaskManagerLogsModal
          isOpen={isLogModalOpen}
          onClose={() => {
            setIsLogModalOpen(false);
            setSelectedTaskForLogs(null);
          }}
          taskId={selectedTaskForLogs || ""}
          isRepetitive={showRepetitiveTasks}
        />
      </div>
    </div>
  );
};

export default TaskManagement;
